import { type SolarDesignDraftState } from './solarDesignDraft';
import {
  reconcileSolarStringPanels,
  type SolarElectricalTopology,
} from './solarElectricalTopology';

/** Newly packed IDs are already minted by the placement operation. Never recover membership by index. */
export const solarElectricalLayout = (
  layout: SolarDesignDraftState['layout'],
  source: SolarElectricalTopology,
  namespace: string,
  panelApplianceId: string | null
): {
  layout: SolarDesignDraftState['layout'];
  electrical: SolarElectricalTopology;
  error?: string;
} => {
  const faceIds = new Set(layout.workingFaces.map((face) => face.id));
  if (layout.workingPanels.some((panel) => !faceIds.has(panel.faceId)))
    return {
      layout,
      electrical: source,
      error: 'Restore the missing roof before editing allocation.',
    };
  const topology = structuredClone(source);
  const faces = new Set(layout.workingFaces.map((face) => face.id));
  topology.placementGroups = topology.placementGroups.filter((group) =>
    faces.has(group.faceId)
  );
  for (const face of layout.workingFaces) {
    if (topology.placementGroups.some((group) => group.faceId === face.id))
      continue;
    // Separate namespaces from the upgrader's ordinal IDs; deterministic per
    // new face so repeated renders before autosave cannot replace identities.
    const selectionId = `${namespace.slice(0, 24)}${(7_000_000 + face.id).toString(16).padStart(12, '0')}`;
    topology.equipmentSelections.push({
      id: selectionId,
      applianceId: panelApplianceId,
      productSpecId: null,
      contentHash: null,
      specSnapshot: null,
    });
    topology.placementGroups.push({
      id: `${namespace.slice(0, 24)}${(6_000_000 + face.id).toString(16).padStart(12, '0')}`,
      faceId: face.id,
      equipmentSelectionId: selectionId,
    });
  }
  const panels = layout.workingPanels.map((panel) => {
    const group =
      topology.placementGroups.find(
        (group) =>
          group.id === panel.placementGroupId && group.faceId === panel.faceId
      ) ??
      topology.placementGroups.find((group) => group.faceId === panel.faceId)!;
    return {
      ...panel,
      placementGroupId: group.id,
      equipmentSelectionId: group.equipmentSelectionId ?? undefined,
    };
  });
  // Retain trusted revisions for Undo; an identity must never be repinned.
  const repacked = layout.workingPanels.some(
    (panel) =>
      !panel.placementGroupId &&
      source.placementGroups.some((group) => group.faceId === panel.faceId)
  );
  const reconciled = reconcileSolarStringPanels(topology, panels, repacked);
  reconciled.validation = null;
  return {
    layout: { ...layout, workingPanels: panels },
    electrical: reconciled,
  };
};
